import _ from 'underscore';

import {assert} from 'Asserts';
import {SCORE_SORTS, Sort, SortKey} from 'search/SearchConstants';
import * as ScrollableTableConstants from 'ui-library/scrollable/ScrollableTableConstants';
import {
  COMPACT_TABLE_PADDING_VERTICAL,
  ScrollableTableColumn,
  SortDirection,
  TABLE_BODY_BORDER_SIZE,
  TABLE_PADDING_VERTICAL,
} from 'ui-library/scrollable/ScrollableTableConstants';

interface ToggleState {
  selectedItemIds: Array<string>;
}

export function computeStateForToggledRows({
  state,
  toggledIds,
  isSelected,
  rowSelectionMode,
}: {
  state: ToggleState;
  toggledIds: Array<string>;
  isSelected: boolean;
  rowSelectionMode?: ScrollableTableConstants.RowSelectionMode | null;
}): ToggleState {
  const newSelectedIds = computeSelectedIdsForToggledRows(
    state.selectedItemIds,
    toggledIds,
    isSelected,
    rowSelectionMode
  );
  return {
    selectedItemIds: newSelectedIds,
  };
}
export function computeSelectedIdsForToggledRows(
  oldSelectedIds: Array<string>,
  toggledIds: Array<string>,
  isSelected: boolean,
  rowSelectionMode?: ScrollableTableConstants.RowSelectionMode | null
): Array<string> {
  if (isSelected) {
    if (rowSelectionMode === ScrollableTableConstants.RowSelectionMode.SINGLE) {
      assert(toggledIds.length === 1, 'Can only select one item when single select');
      return toggledIds;
    }
    // eslint-disable-next-line no-restricted-syntax
    const newToggledIds = toggledIds.filter((toggledId) => !_.contains(oldSelectedIds, toggledId));
    return [...oldSelectedIds, ...newToggledIds];
  }
  // eslint-disable-next-line no-restricted-syntax
  return oldSelectedIds.filter((id) => !_.contains(toggledIds, id));
}

export function cycleColumnHeaderSort(currentSorts: Array<Sort>, selectedSortKey: string): Sort {
  // If we're sorting by created or modified at, we want to sort by descending first, then by ascending
  const shouldDefaultToReverse = [
    SortKey.SORT_CREATED_AT_KEY as string,
    SortKey.SORT_MODIFIED_AT_KEY as string,
  ].includes(selectedSortKey);

  const currentSort = currentSorts.find((sort) => sort.key === selectedSortKey);
  if (currentSort) {
    return {key: currentSort.key, reverse: !currentSort.reverse};
  }
  return {key: selectedSortKey, reverse: shouldDefaultToReverse};
}

export function getColumnHeaderSortDirection(
  currentSorts: Array<Sort>,
  sortKey: string
): SortDirection | null {
  const currentSort = currentSorts.find((c) => c.key === sortKey);
  if (!currentSort) {
    return null;
  }
  // For score sorts, such as chemical similarity sorts, default order is descending and reverse is ascending.
  const shouldFlipDirection = SCORE_SORTS.includes(currentSort.key as SortKey);
  if ((currentSort.reverse && !shouldFlipDirection) || (!currentSort.reverse && shouldFlipDirection)) {
    return ScrollableTableConstants.DESCENDING_SORT;
  } else {
    return ScrollableTableConstants.ASCENDING_SORT;
  }
}

export function cycleSortDirection(currentSortDirection: SortDirection): SortDirection {
  return currentSortDirection === SortDirection.ASCENDING_SORT
    ? SortDirection.DESCENDING_SORT
    : SortDirection.ASCENDING_SORT;
}

export function makeFirstColumnFixed(columns: Array<ScrollableTableColumn>): Array<ScrollableTableColumn> {
  if (columns.length > 0) {
    columns[0] = {...columns[0], isFixed: true};
  }
  return columns;
}

/**
 * The set of columns a user is allowed to reorder for a given table: everything except the primary
 * (first) column and any right-pinned columns. This matches the positional assumptions the table
 * relies on elsewhere (the primary column hosts the checkbox / column controls and must stay at
 * index 0; `isFixedRight` columns are pinned to the right edge).
 */
export function getReorderableColumns(columns: Array<ScrollableTableColumn>): Array<ScrollableTableColumn> {
  return columns.slice(1).filter((column) => !column.isFixedRight);
}

/**
 * Reorders `columns` to reflect a user-saved order (a list of `accessorKey`s).
 *
 * The saved order is treated as a partial ordering hint, not an exhaustive list: the primary
 * (first) column stays pinned at index 0 and right-pinned (`isFixedRight`) columns stay at the end,
 * while the remaining "middle" columns are sorted by their position in `orderedColumnKeys`. Columns
 * absent from the saved order (e.g. dynamic `extra_field:` / `result_field:` columns, or
 * type-specific columns that only appear for some searches) keep their original relative order and
 * sort after any saved columns. This tolerates saved keys that are not present in the current set.
 */
export function applyColumnOrder(
  columns: Array<ScrollableTableColumn>,
  orderedColumnKeys: Array<string>
): Array<ScrollableTableColumn> {
  if (columns.length <= 1 || orderedColumnKeys.length === 0) {
    return columns;
  }
  const primary = columns[0];
  const rest = columns.slice(1);
  const fixedRight = rest.filter((column) => column.isFixedRight);
  const middle = rest.filter((column) => !column.isFixedRight);

  const savedIndexByKey = new Map(orderedColumnKeys.map((key, index) => [key, index]));
  const orderRank = (column: ScrollableTableColumn): number =>
    savedIndexByKey.has(column.accessorKey) ? savedIndexByKey.get(column.accessorKey)! : Infinity;

  // Stable sort: `_.sortBy` preserves the original relative order for equal ranks, so unsaved
  // columns retain their incoming order after the saved ones.
  const orderedMiddle = _.sortBy(middle, orderRank);

  return [primary, ...orderedMiddle, ...fixedRight];
}

export function getRowHeightBuffer(isCompact: boolean): number {
  const padding = isCompact ? COMPACT_TABLE_PADDING_VERTICAL : TABLE_PADDING_VERTICAL;
  return padding * 2 + TABLE_BODY_BORDER_SIZE;
}
